import { createMaterialTopTabNavigator } from "@react-navigation/material-top-tabs";
import { DarkTheme, DefaultTheme, NavigationContainer, NavigationIndependentTree } from "@react-navigation/native";
import { router, useGlobalSearchParams } from "expo-router";
import { useTheme } from "@/hooks/useTheme";
import { View, Animated, LayoutChangeEvent } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LocationTab, FilesTab, UsersTab, SupervisorsTab, InfoTab } from "./tabs";
import { ShiftLocationType } from "@/types/db/shifts";
import { useState, useRef, useCallback, useEffect, useMemo } from "react";

import { ShiftUser } from "@/types/db/shift_users";

type TabName = "location" | "files" | "team" | "supervisors" | "info";

type IconName = "map-marker-outline" | "paperclip" | "account-group" | "shield-crown" | "information-outline";

const TAB_CONFIG: Record<TabName, { title: string; icon?: IconName }> = {
  location: { title: "Location", icon: "map-marker-outline" },
  files: { title: "Files", icon: "paperclip" },
  team: { title: "Team", icon: "account-group" },
  supervisors: { title: "Supervisors", icon: "shield-crown" },
  info: { title: "Info", icon: "information-outline" },
};

interface Attachment {
  id: string;
  name: string;
  size: number;
  type: string;
  url: string;
  bucket: string;
  uploadedAt: string;
  uploadedBy: string;
}

interface ShiftSupervisor {
  id: string;
  user_id: string;
  user?: {
    id: string;
    first_name: string;
    last_name: string;
    email_address?: string;
    preferred_name?: string;
  };
}

interface MetaTabProps {
  width?: number;
  location?: ShiftLocationType;
  projectLocation?: ShiftLocationType;
  attachments?: Attachment[];
  shiftUsers?: ShiftUser[];
  shiftSupervisors?: ShiftSupervisor[];
  isSupervisor?: boolean;
  shiftDescription?: string;
  projectDescription?: string;
  projectName?: string;
  dressCode?: { id: string; name: string; description?: string };
  abilities?: { id: string; name: string }[];
  restrictions?: { id: string; name: string }[];
  onsiteContact?: {
    name: string;
    phone_number?: string;
    email_address?: string;
  };
  // Decline shift action
  canDecline?: boolean;
  onDecline?: () => void;
  declining?: boolean;
  // For navigation to user/supervisor details
  shiftId?: string;
}

// Wrapper component to measure tab content height
function MeasuredTab({
  children,
  tabName,
  onHeightChange,
}: {
  children: React.ReactNode;
  tabName: TabName;
  onHeightChange: (tab: TabName, height: number) => void;
}) {
  const handleLayout = useCallback(
    (e: LayoutChangeEvent) => {
      const { height } = e.nativeEvent.layout;
      onHeightChange(tabName, height);
    },
    [tabName, onHeightChange],
  );

  return <View onLayout={handleLayout}>{children}</View>;
}

export function MetaTab({
  width,
  location,
  projectLocation,
  attachments,
  shiftUsers,
  shiftSupervisors,
  isSupervisor = false,
  shiftDescription,
  projectDescription,
  projectName,
  dressCode,
  abilities,
  restrictions,
  onsiteContact,
  canDecline,
  onDecline,
  declining,
  shiftId,
}: MetaTabProps) {
  // Create navigator per-instance so it cleans up on unmount (prevents CPU leak)
  const TopTabs = useMemo(() => createMaterialTopTabNavigator(), []);

  const { colors, theme } = useTheme();
  const params = useGlobalSearchParams<{ tab?: string }>();

  const showLabel = (width ?? 0) >= 600;

  // Track heights for each tab
  const [tabHeights, setTabHeights] = useState<Record<TabName, number>>({
    location: 100,
    files: 100,
    team: 100,
    supervisors: 100,
    info: 100,
  });

  // Track current tab
  const [currentTab, setCurrentTab] = useState<TabName>("location");

  // Animated height value
  const animatedHeight = useRef(new Animated.Value(100)).current;

  // Build tabs array based on isSupervisor
  const visibleTabs: TabName[] = isSupervisor
    ? ["location", "files", "team", "info"]
    : ["location", "files", "supervisors", "info"];

  const getValidInitialRoute = (requestedTab?: string): TabName => {
    const tab = requestedTab?.toLowerCase() as TabName;
    return visibleTabs.includes(tab) ? tab : "location";
  };

  // Initialize current tab from params
  useEffect(() => {
    const initialTab = getValidInitialRoute(params.tab);
    setCurrentTab(initialTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle height changes from tabs
  const handleHeightChange = useCallback((tab: TabName, height: number) => {
    setTabHeights((prev) => {
      if (prev[tab] === height) return prev;
      return { ...prev, [tab]: height };
    });
  }, []);

  // Animate height when tab changes or height updates
  useEffect(() => {
    const targetHeight = tabHeights[currentTab] || 100;
    Animated.spring(animatedHeight, {
      toValue: targetHeight + 50,
      useNativeDriver: false,
      tension: 100,
      friction: 12,
    }).start();
  }, [currentTab, tabHeights, animatedHeight]);

  // Handle tab change
  const handleTabChange = useCallback((tabName: string) => {
    setCurrentTab(tabName as TabName);
    router.setParams({ tab: tabName.toLowerCase() });
  }, []);

  // Create tab components with measurement wrapper
  const createMeasuredComponent = (tabName: TabName, content: React.ReactNode): React.ComponentType => {
    const Component = () => (
      <MeasuredTab tabName={tabName} onHeightChange={handleHeightChange}>
        {content}
      </MeasuredTab>
    );
    Component.displayName = `MeasuredTab(${tabName})`;
    return Component;
  };

  const componentMap: Record<TabName, React.ComponentType> = {
    location: createMeasuredComponent(
      "location",
      <LocationTab location={location} projectLocation={projectLocation} />,
    ),
    files: createMeasuredComponent("files", <FilesTab attachments={attachments} />),
    team: createMeasuredComponent(
      "team",
      <UsersTab shiftUsers={(shiftUsers ?? []).filter((u) => u.status === "ACCEPTED")} shiftId={shiftId} />,
    ),
    supervisors: createMeasuredComponent(
      "supervisors",
      <SupervisorsTab supervisors={shiftSupervisors} shiftId={shiftId} />,
    ),
    info: createMeasuredComponent(
      "info",
      <InfoTab
        shiftDescription={shiftDescription}
        projectDescription={projectDescription}
        projectName={projectName}
        abilities={abilities}
        restrictions={restrictions}
        onsiteContact={onsiteContact}
        isSupervisor={isSupervisor}
        shiftId={shiftId}
        canDecline={canDecline}
        onDecline={onDecline}
        declining={declining}
      />,
    ),
  };

  return (
    <Animated.View style={{ height: animatedHeight, maxHeight: 280 }}>
      <NavigationIndependentTree>
        <NavigationContainer
          theme={{
            dark: theme.name === "dark",
            fonts: theme.name === "dark" ? DarkTheme.fonts : DefaultTheme.fonts,
            colors: {
              background: theme.card,
              border: theme.border,
              card: theme.card,
              primary: theme.primary,
              text: theme.text,
              notification: theme.name === "dark" ? DarkTheme.colors.notification : DefaultTheme.colors.notification,
            },
          }}
        >
          <TopTabs.Navigator
            initialRouteName={getValidInitialRoute(params.tab)}
            style={{ marginHorizontal: -20, marginBottom: 0 }}
            screenListeners={({ route }) => ({
              tabPress: () => handleTabChange(route.name),
              focus: () => handleTabChange(route.name),
            })}
            screenOptions={{
              // sceneStyle: { overflowY: "scroll" },
              animationEnabled: true,
              lazy: true,
              lazyPreloadDistance: 0,
              swipeEnabled: true,
              tabBarShowIcon: false,
              tabBarActiveTintColor: colors.primary,
              tabBarInactiveTintColor: colors.placeholder,
            }}
          >
            {visibleTabs.map((tabName) => {
              const config = TAB_CONFIG[tabName];
              const Component = componentMap[tabName];
              return (
                <TopTabs.Screen
                  key={tabName}
                  name={tabName}
                  component={Component}
                  options={{
                    tabBarLabel: ({ color }) => (
                      <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
                        {config.icon && <MaterialCommunityIcons name={config.icon} size={18} color={color} />}
                        {showLabel && (
                          <ThemedText style={{ color, fontSize: 12, fontWeight: "600" }}>{config.title}</ThemedText>
                        )}
                      </View>
                    ),
                  }}
                />
              );
            })}
          </TopTabs.Navigator>
        </NavigationContainer>
      </NavigationIndependentTree>
    </Animated.View>
  );
}
